'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { businessInfo as business } from '@/content/globals';

export default function Sidebar() {
  const [reviews, setReviews] = useState([]);

  // Define your service areas as an array of objects
  const serviceAreas = [
    {
      name: 'Chattanooga, TN',
      href: '/service-area/chattanooga-car-detailing',
    },
    { name: 'Cleveland, TN', href: '/service-area/cleveland-car-detailing' },
    {
      name: 'Collegedale, TN',
      href: '/service-area/collegedale-car-detailing',
    },
    {
      name: 'East Ridge, TN',
      href: '/service-area/east-ridge-car-detailing',
    },
    { name: 'Ooltewah, TN', href: '/service-area/ooltewah-car-detailing' },
    { name: 'Red Bank, TN', href: '/service-area/red-bank-car-detailing' },
    {
      name: 'Soddy Daisy, TN',
      href: '/service-area/soddy-daisy-car-detailing',
    },
  ];

  // Sort the service areas alphabetically by name
  const sortedServiceAreas = [...serviceAreas].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch('/api/google-reviews');
        const data = await res.json();
        setReviews(data.reviews);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    }
    fetchReviews();
  }, []);

  const latestFiveStarReview = Array.isArray(reviews)
    ? reviews
        .filter((review) => review.rating === 5)
        .sort((a, b) => b.time - a.time)[0]
    : null;

  return (
    <aside className="tablet:w-1/4 p-4">
      {/* Operating Hours */}
      <details open className="slide-toggle mb-4">
        <summary className="bg-primary flex cursor-pointer items-center justify-between rounded px-4 py-2 text-white">
          <span>Operating Hours</span>
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </summary>
        <div className="mt-2">
          <table className="w-full border border-gray-300 text-xs">
            <tbody>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Monday</td>
                <td className="px-2 py-2 text-right">8:30am - 5:30pm</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Tuesday</td>
                <td className="px-2 py-2 text-right">8:30am - 5:30pm</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Wednesday</td>
                <td className="px-2 py-2 text-right">8:30am - 5:30pm</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Thursday</td>
                <td className="px-2 py-2 text-right">8:30am - 5:30pm</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Friday</td>
                <td className="px-2 py-2 text-right">8:30am - 5:30pm</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="px-2 py-2">Saturday</td>
                <td className="px-2 py-2 text-right">8:30am - 1:00pm</td>
              </tr>
              <tr>
                <td className="px-2 py-2">Sunday</td>
                <td className="px-2 py-2 text-right">Closed</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>

      {/* Testimonials / Reviews */}
      <details open className="slide-toggle mb-4">
        <summary className="bg-primary flex cursor-pointer items-center justify-between rounded px-4 py-2 text-white">
          <span>Testimonials</span>
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </summary>
        <div className="mt-2 pl-2">
          {reviews.length > 0 ? (
            reviews.slice(0, 3).map((review, index) => (
              <div key={index} className="mb-6 border-b border-gray-200 pb-4">
                {/* Header: Stars, Reviewer Name & Timestamp */}
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center">
                    {/* Assuming all reviews are 5 star. Otherwise you could map the rating */}
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="h-4 w-4 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.97c.3.921-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.197-1.54-1.118l1.287-3.97a1 1 0 00-.364-1.118L2.05 9.397c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.97z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-xs text-gray-600">
                    {review.relative_time_description}
                  </span>
                </div>
                {/* Reviewer Name */}
                <p className="text-sm font-semibold">{review.author_name}</p>
                {/* Review Text clamped to 3 lines */}
                <p className="line-clamp-3 text-sm break-words whitespace-pre-wrap">
                  {review.text}
                </p>
                {/* Read More Link */}
                <a
                  href={review.author_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                  aria-label="View Detailed Google Reviews"
                >
                  Read the Google Review
                </a>
              </div>
            ))
          ) : (
            <p className="text-sm">Loading reviews...</p>
          )}
        </div>
      </details>

      {/* Location */}
      <details open className="slide-toggle mb-4">
        <summary className="bg-primary flex cursor-pointer items-center justify-between rounded px-4 py-2 text-white">
          <span>Location</span>
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </summary>
        <div className="mt-2 pl-2">
          <p className="mb-2 text-sm">
            3921 Weldon Dr, {business.address.city}, TN 37412
          </p>
          <iframe
            title="Google Map Location"
            className="h-64 w-full border-0"
            loading="lazy"
            allowFullScreen
            src="https://www.google.com/maps/embed/v1/place?key=AIzaSyCVlsTEt0B7ZMpqIj0sbikxRFzMTzrsOto&q=Mayes+Auto+Detailing"
          ></iframe>
        </div>
      </details>

      {/* Proudly Serving Service Areas */}
      <details open className="slide-toggle mb-4">
        <summary className="bg-primary flex cursor-pointer items-center justify-between rounded px-4 py-2 text-white">
          <span>Proudly Serving</span>
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </summary>
        <div className="mt-2 pl-2">
          <ul className="space-y-1 text-sm">
            {sortedServiceAreas.map((area, index) => (
              <li key={index}>
                <Link
                  href={area.href}
                  className="hover:text-primary transition-colors"
                >
                  {area.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </aside>
  );
}
